import React from 'react'
import PropTypes from 'prop-types'
import {
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  Avatar,
  withStyles,
  FormHelperText,
} from '@material-ui/core'

function formatLocaleDateTime(dateStr) {
  try {
    if (dateStr && !/Z$/.test(dateStr)) {
      dateStr += 'Z'
    }
    return new Date(dateStr).toLocaleString(navigator.language || 'en-US', {
      dateStyle: 'short',
      timeStyle: 'short',
    })
  } catch (e) {
    return dateStr
  }
}

const commentsStyles = (theme) => ({
  commentsContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    scrollbarWidth: 'thin',
    scrollbarColor: '#c1c1c1 transparent',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#c1c1c1',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#a1a1a1',
    },
  },
  sectionTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    color: '#333',
    fontSize: '1rem',
    marginTop: theme.spacing(1),
  },
  avatar: {
    backgroundColor: theme.palette.secondary.main,
    marginRight: theme.spacing(1),
  },
  commentCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: '#fff',
  },
  commentInputContainer: {
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    borderTop: '1px solid #e0e0e0',
    position: 'absolute',
    paddingBottom: '20px',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  commentInput: {
    backgroundColor: '#fff',
    '& .MuiOutlinedInput-root': {
      transition: 'all 0.3s ease',
      '&:hover': {
        borderColor: '#ac0000',
      },
      '&.Mui-focused': {
        borderColor: '#ac0000',
      },
    },
  },
  commentInputExpanded: {
    minHeight: '120px',
  },
  commentActions: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  addButton: {
    backgroundColor: '#ac0000',
    color: '#fff',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#C62828',
    },
    '&:disabled': {
      backgroundColor: '#ccc',
    },
  },
  cancelButton: {
    color: '#666',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
})

class CommentsSection extends React.Component {
  commentsEndRef = React.createRef()
  state = {
    commentText: '',
    isExpanded: false,
    isSubmitting: false,
  }

  handleCommentChange = (event) => {
    this.setState({ commentText: event.target.value })
  }

  scrollToBottom = () => {
    if (this.commentsEndRef.current) {
      this.commentsEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  handleCommentFocus = () => {
    this.setState({ isExpanded: true }, () => {
      setTimeout(() => {
        this.scrollToBottom()
      }, 50)
    })
  }

  resetCommentBox = () => {
    this.setState({
      commentText: '',
      isExpanded: false,
    })
  }

  handleAddComment = async () => {
    const { commentText } = this.state
    const { ticketKey, onAddComment } = this.props

    if (!commentText.trim()) return

    this.setState({ isSubmitting: true })

    try {
      // Call the parent's add comment function
      await onAddComment(ticketKey, commentText.trim())
      this.resetCommentBox()
    } catch (error) {
      console.error('Failed to add comment:', error)
    } finally {
      this.setState({ isSubmitting: false })
    }
  }

  handleCancel = () => {
    this.resetCommentBox()
  }

  render() {
    const { classes, comments, totalComments, ticketKey } = this.props
    const { commentText, isExpanded, isSubmitting } = this.state
    const maxCharacters = 32767

    return (
      <>
        <Typography variant="h6" className={classes.sectionTitle}>
          Comments ({totalComments || 0})
        </Typography>

        {/* Scrollable Comments List */}
        <div
          className={classes.commentsContainer}
          style={{
            paddingBottom: this.state.isExpanded ? '170px' : '50px',
          }}
        >
          {comments && comments.length > 0 ? (
            comments.map((comment, index) => (
              <Paper key={index} elevation={2} className={classes.commentCard}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <Avatar className={classes.avatar}>
                      {comment.author?.display_name?.charAt(0) || 'A'}
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="body1">
                      <strong>
                        {comment.author?.display_name || 'Unknown'}
                      </strong>
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {formatLocaleDateTime(comment.created_date)}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography
                  variant="body2"
                  style={{ marginTop: 8, whiteSpace: 'pre-line' }}
                >
                  {comment.body}
                </Typography>
              </Paper>
            ))
          ) : (
            <Paper elevation={1} className={classes.commentCard}>
              <Typography variant="body2" color="textSecondary" align="center">
                No comments yet. Be the first to add a comment.
              </Typography>
            </Paper>
          )}
          <div ref={this.commentsEndRef} />
        </div>

        {/* Static Comment Input Bar */}
        {ticketKey.startsWith('GGASUPPORT-') && (
          <div className={classes.commentInputContainer}>
            <TextField
              className={classes.commentInput}
              placeholder="Add a comment..."
              variant="outlined"
              fullWidth
              multiline
              rows={isExpanded ? 4 : 1}
              value={commentText}
              onChange={this.handleCommentChange}
              onFocus={this.handleCommentFocus}
              disabled={isSubmitting}
              inputProps={{ maxLength: maxCharacters }}
            />
            {isExpanded && (
              <FormHelperText
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color:
                    commentText.length > maxCharacters - 1
                      ? '#f44336'
                      : 'rgba(0, 0, 0, 0.6)',
                  marginTop: 4,
                }}
              >
                <span>
                  {commentText.length > maxCharacters - 1
                    ? 'Comment exceeds the maximum allowed characters'
                    : ''}
                </span>
                <span>{`${commentText.length}/${maxCharacters - 1}`}</span>
              </FormHelperText>
            )}
            {isExpanded && (
              <div className={classes.commentActions}>
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Button
                    onClick={this.handleCancel}
                    className={classes.cancelButton}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={this.handleAddComment}
                    className={classes.addButton}
                    disabled={
                      !commentText.trim() ||
                      isSubmitting ||
                      commentText.length >= maxCharacters
                    }
                  >
                    {isSubmitting ? 'Adding...' : 'Add'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </>
    )
  }
}

CommentsSection.propTypes = {
  classes: PropTypes.object.isRequired,
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      author: PropTypes.shape({
        display_name: PropTypes.string,
      }),
      body: PropTypes.string,
      created_date: PropTypes.string,
    })
  ),
  totalComments: PropTypes.number,
  ticketKey: PropTypes.string.isRequired,
  onAddComment: PropTypes.func.isRequired,
}

CommentsSection.defaultProps = {
  comments: [],
  totalComments: 0,
}

export default withStyles(commentsStyles)(CommentsSection)
